import axiosInstance from "./axiosConfig";
import { handleAPIError } from "./utils/errors";
import {
  AssociateIndividualsRequest,
  ClientWarningDismissal,
  ClientWarningKey,
  CreateForeignNationalRequest,
  ForeignNational,
  UpdateForeignNationalRequest
} from "./types/foreignNational";
import { IndividualApiResponse } from "./individuals";
import type {
  CreateForeignNationalAddressHistoryEntryRequest,
  ForeignNationalAddressHistoryEntry,
  UpdateForeignNationalAddressHistoryEntryRequest
} from "./types/foreignNationalAddressHistory";
import type {
  AutofillApplyRequest,
  AutofillProposals,
  EducationPayload,
  ForeignNationalEducationHistoryEntry,
  ForeignNationalPassport,
  PassportPayload
} from "./types/foreignNationalRecords";
import type {
  CreateForeignNationalEmploymentHistoryEntryRequest,
  ForeignNationalEmploymentHistoryEntry,
  UpdateForeignNationalEmploymentHistoryEntryRequest
} from "./types/foreignNationalEmploymentHistory";
import type {
  CreateForeignNationalImmigrationEntryExitHistoryEntryRequest,
  CreateForeignNationalImmigrationStatusHistoryEntryRequest,
  ForeignNationalImmigrationEntryExitHistoryEntry,
  ForeignNationalImmigrationStatusHistoryEntry,
  UpdateForeignNationalImmigrationEntryExitHistoryEntryRequest,
  UpdateForeignNationalImmigrationStatusHistoryEntryRequest
} from "./types/foreignNationalImmigrationHistory";
import type {
  CreateForeignNationalDependentRequest,
  ForeignNationalDependent,
  UpdateForeignNationalDependentRequest
} from "./types/foreignNationalDependent";
import type {
  CreateForeignNationalReminderRequest,
  ForeignNationalReminder,
  UpdateForeignNationalReminderRequest
} from "./types/foreignNationalReminder";
import type {
  ClientFilterCounts,
  ClientListFilter,
  DuplicateMatch,
  DuplicateQuery,
  MergePreview,
  MergeResult,
  PassportScan
} from "./types/clientDirectory";
import type {
  ForeignNationalActivityLogEvent,
  ForeignNationalActivityLogPage,
  ForeignNationalActivitySubscription,
  ListForeignNationalActivityLogParams
} from "./types/foreignNationalActivityLog";

const BASE_URL = "/foreign_nationals";

const recordRequest = async <T>(
  run: () => Promise<{ data: T }>
): Promise<T> => {
  try {
    return (await run()).data;
  } catch (error) {
    const apiError = handleAPIError(error);
    throw apiError;
  }
};

export type ForeignNationalAuthStatus = {
  uid: string;
  exists: boolean;
  disabled: boolean;
  /** The sign-in email on the login user. */
  email?: string | null;
  last_sign_in?: string | null;
};

export type ForeignNationalNoteSource = "person" | "assistant" | "import";

/** One entry on a client's notes timeline (`/foreign_nationals/:id/notes`); the assistant reads them too. */
export type ForeignNationalNote = {
  id: string;
  foreign_national_id: string;
  /** `null` for the assistant's entries; `approved_by` signs those. */
  author_uid: string | null;
  author_name: string | null;
  body: string;
  pinned: boolean;
  /** The linked case's `individual_id`. */
  case_id: string | null;
  source: ForeignNationalNoteSource;
  approved_by: string | null;
  approved_by_name: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** When the body last changed (the Edited marker). */
  edited_at: string | null;
};

export type ForeignNationalNotesList = {
  notes: ForeignNationalNote[];
  max_chars: number;
};

/** An assistant suggestion waiting on a person; also shown as a card in its chat. */
export type ForeignNationalNoteProposal = {
  approval_id: string;
  thread_id: string;
  thread_title: string | null;
  scope_kind: string;
  scope_id: string;
  case_id: string | null;
  note: string;
  proposed_at: string | null;
  /** False when the pause asks about other actions too: answer it in the chat. */
  standalone: boolean;
};

export type NoteSearchResult = ForeignNationalNote & { client_name: string };

export type ForeignNationalAssignmentFilter = "all" | "unassigned";

export type ForeignNationalSort =
  | "newest"
  | "oldest"
  | "firstNameAsc"
  | "firstNameDesc"
  | "lastNameAsc"
  | "lastNameDesc";

export type ListForeignNationalsParams = {
  limit?: number;
  offset?: number;
  q?: string;
  include_all_practices?: boolean;
  assignment_filter?: ForeignNationalAssignmentFilter;
  assigned_user_uid?: string;
  sort?: ForeignNationalSort;
  filter?: ClientListFilter;
  /**
   * Adds `list_meta` (status, deadline, portal, duplicates) to each row.
   * `"duplicates"` returns only `possible_duplicates`, which is much cheaper.
   */
  include_meta?: boolean | "duplicates";
};

export type ListForeignNationalsResponse = {
  foreign_nationals: ForeignNational[];
  total_count: number;
  assigned_count: number;
  filter_counts?: ClientFilterCounts | null;
};

export const ForeignNationalsAPI = {
  async list(
    params: ListForeignNationalsParams = {}
  ): Promise<ListForeignNationalsResponse> {
    try {
      const response = await axiosInstance.get<ListForeignNationalsResponse>(
        BASE_URL,
        { params }
      );
      const raw = response.data;
      return {
        foreign_nationals: Array.isArray(raw?.foreign_nationals)
          ? raw.foreign_nationals
          : [],
        total_count: Number.isFinite(raw?.total_count) ? raw.total_count : 0,
        assigned_count: Number.isFinite(raw?.assigned_count)
          ? raw.assigned_count
          : 0,
        filter_counts: raw?.filter_counts ?? null
      };
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** Existing clients that look like the one being created. */
  /** Read a passport photo page into new-client values. Stores nothing. */
  async scanPassport(file: File): Promise<PassportScan> {
    const formData = new FormData();
    formData.append("file", file);
    return recordRequest(() =>
      axiosInstance.post<PassportScan>(`${BASE_URL}/passport_scan`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      })
    );
  },

  async findDuplicates(query: DuplicateQuery): Promise<DuplicateMatch[]> {
    const data = await recordRequest(() =>
      axiosInstance.get<{ duplicates: DuplicateMatch[] }>(
        `${BASE_URL}/duplicates`,
        { params: query }
      )
    );
    return data.duplicates ?? [];
  },

  /** Where a merged-away client now lives, or null. */
  async getMergedInto(
    foreignNationalId: string
  ): Promise<{ merged_into_id: string; name: string } | null> {
    try {
      const response = await axiosInstance.get<{
        merged_into_id: string | null;
        name: string;
      }>(`${BASE_URL}/${foreignNationalId}/merged_into`);
      return response.data.merged_into_id
        ? {
            merged_into_id: response.data.merged_into_id,
            name: response.data.name
          }
        : null;
    } catch {
      return null;
    }
  },

  async mergePreview(
    targetId: string,
    sourceId: string
  ): Promise<MergePreview> {
    return recordRequest(() =>
      axiosInstance.get<MergePreview>(`${BASE_URL}/${targetId}/merge`, {
        params: { source_id: sourceId }
      })
    );
  },

  async merge(
    targetId: string,
    sourceId: string,
    takeSource: string[]
  ): Promise<MergeResult> {
    return recordRequest(() =>
      axiosInstance.post<MergeResult>(`${BASE_URL}/${targetId}/merge`, {
        source_id: sourceId,
        take_source: takeSource
      })
    );
  },

  async setArchived(
    foreignNationalId: string,
    archived: boolean
  ): Promise<ForeignNational> {
    return recordRequest(() =>
      axiosInstance.post<ForeignNational>(
        `${BASE_URL}/${foreignNationalId}/archive`,
        { archived }
      )
    );
  },

  async get(foreignNationalId: string): Promise<ForeignNational> {
    try {
      const response = await axiosInstance.get<ForeignNational>(
        `${BASE_URL}/${foreignNationalId}`
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async create(
    payload: CreateForeignNationalRequest
  ): Promise<ForeignNational> {
    try {
      const response = await axiosInstance.post<ForeignNational>(
        BASE_URL,
        payload
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async associateIndividuals(
    foreignNationalId: string,
    payload: AssociateIndividualsRequest
  ): Promise<IndividualApiResponse[]> {
    try {
      const response = await axiosInstance.post<IndividualApiResponse[]>(
        `${BASE_URL}/${foreignNationalId}/associate_individuals`,
        payload
      );
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async update(
    foreignNationalId: string,
    payload: UpdateForeignNationalRequest
  ): Promise<ForeignNational> {
    try {
      const response = await axiosInstance.put<ForeignNational>(
        `${BASE_URL}/${foreignNationalId}`,
        payload
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async delete(foreignNationalId: string): Promise<{
    message?: string;
  }> {
    try {
      const response = await axiosInstance.delete<{ message?: string }>(
        `${BASE_URL}/${foreignNationalId}`
      );
      return response.data ?? {};
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async uploadAvatar(
    foreignNationalId: string,
    file: File
  ): Promise<{ success: boolean }> {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axiosInstance.post<{ success: boolean }>(
        `${BASE_URL}/${foreignNationalId}/avatar`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" }
        }
      );
      return response.data ?? { success: true };
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async listDependents(
    foreignNationalId: string
  ): Promise<ForeignNationalDependent[]> {
    try {
      const response = await axiosInstance.get<ForeignNationalDependent[]>(
        `${BASE_URL}/${foreignNationalId}/dependents`
      );
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async createDependent(
    foreignNationalId: string,
    payload: CreateForeignNationalDependentRequest
  ): Promise<ForeignNationalDependent> {
    try {
      const response = await axiosInstance.post<ForeignNationalDependent>(
        `${BASE_URL}/${foreignNationalId}/dependents`,
        payload
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async updateDependent(
    foreignNationalId: string,
    dependentId: string,
    payload: UpdateForeignNationalDependentRequest
  ): Promise<ForeignNationalDependent> {
    try {
      const response = await axiosInstance.put<ForeignNationalDependent>(
        `${BASE_URL}/${foreignNationalId}/dependents/${dependentId}`,
        payload
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async deleteDependent(
    foreignNationalId: string,
    dependentId: string
  ): Promise<{ message?: string }> {
    try {
      const response = await axiosInstance.delete<{ message?: string }>(
        `${BASE_URL}/${foreignNationalId}/dependents/${dependentId}`
      );
      return response.data ?? {};
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async listReminders(
    foreignNationalId: string
  ): Promise<ForeignNationalReminder[]> {
    try {
      const response = await axiosInstance.get<ForeignNationalReminder[]>(
        `${BASE_URL}/${foreignNationalId}/reminders`
      );
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async createReminder(
    foreignNationalId: string,
    payload: CreateForeignNationalReminderRequest
  ): Promise<ForeignNationalReminder> {
    try {
      const response = await axiosInstance.post<ForeignNationalReminder>(
        `${BASE_URL}/${foreignNationalId}/reminders`,
        payload
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async updateReminder(
    foreignNationalId: string,
    reminderId: string,
    patch: UpdateForeignNationalReminderRequest
  ): Promise<ForeignNationalReminder> {
    try {
      const response = await axiosInstance.put<ForeignNationalReminder>(
        `${BASE_URL}/${foreignNationalId}/reminders/${reminderId}`,
        patch
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async deleteReminder(
    foreignNationalId: string,
    reminderId: string
  ): Promise<{ message?: string }> {
    try {
      const response = await axiosInstance.delete<{ message?: string }>(
        `${BASE_URL}/${foreignNationalId}/reminders/${reminderId}`
      );
      return response.data ?? {};
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async recomputeReminders(
    foreignNationalId: string
  ): Promise<Record<string, unknown>> {
    try {
      const response = await axiosInstance.post<Record<string, unknown>>(
        `${BASE_URL}/${foreignNationalId}/reminders/recompute`
      );
      return response.data ?? {};
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async listAddressHistoryEntries(
    foreignNationalId: string
  ): Promise<ForeignNationalAddressHistoryEntry[]> {
    try {
      const response = await axiosInstance.get<
        ForeignNationalAddressHistoryEntry[]
      >(`${BASE_URL}/${foreignNationalId}/address_history`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async createAddressHistoryEntry(
    foreignNationalId: string,
    payload: CreateForeignNationalAddressHistoryEntryRequest
  ): Promise<ForeignNationalAddressHistoryEntry> {
    try {
      const response =
        await axiosInstance.post<ForeignNationalAddressHistoryEntry>(
          `${BASE_URL}/${foreignNationalId}/address_history`,
          payload
        );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async updateAddressHistoryEntry(
    foreignNationalId: string,
    addressHistoryId: string,
    payload: UpdateForeignNationalAddressHistoryEntryRequest
  ): Promise<ForeignNationalAddressHistoryEntry> {
    try {
      const response =
        await axiosInstance.put<ForeignNationalAddressHistoryEntry>(
          `${BASE_URL}/${foreignNationalId}/address_history/${addressHistoryId}`,
          payload
        );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async deleteAddressHistoryEntry(
    foreignNationalId: string,
    addressHistoryId: string
  ): Promise<{ message?: string }> {
    try {
      const response = await axiosInstance.delete<{ message?: string }>(
        `${BASE_URL}/${foreignNationalId}/address_history/${addressHistoryId}`
      );
      return response.data ?? {};
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async listEmploymentHistoryEntries(
    foreignNationalId: string
  ): Promise<ForeignNationalEmploymentHistoryEntry[]> {
    try {
      const response = await axiosInstance.get<
        ForeignNationalEmploymentHistoryEntry[]
      >(`${BASE_URL}/${foreignNationalId}/employment_history`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async createEmploymentHistoryEntry(
    foreignNationalId: string,
    payload: CreateForeignNationalEmploymentHistoryEntryRequest
  ): Promise<ForeignNationalEmploymentHistoryEntry> {
    try {
      const response =
        await axiosInstance.post<ForeignNationalEmploymentHistoryEntry>(
          `${BASE_URL}/${foreignNationalId}/employment_history`,
          payload
        );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async updateEmploymentHistoryEntry(
    foreignNationalId: string,
    employmentHistoryId: string,
    payload: UpdateForeignNationalEmploymentHistoryEntryRequest
  ): Promise<ForeignNationalEmploymentHistoryEntry> {
    try {
      const response =
        await axiosInstance.put<ForeignNationalEmploymentHistoryEntry>(
          `${BASE_URL}/${foreignNationalId}/employment_history/${employmentHistoryId}`,
          payload
        );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async deleteEmploymentHistoryEntry(
    foreignNationalId: string,
    employmentHistoryId: string
  ): Promise<{ message?: string }> {
    try {
      const response = await axiosInstance.delete<{ message?: string }>(
        `${BASE_URL}/${foreignNationalId}/employment_history/${employmentHistoryId}`
      );
      return response.data ?? {};
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async listPassports(
    foreignNationalId: string
  ): Promise<ForeignNationalPassport[]> {
    return recordRequest(() =>
      axiosInstance.get<ForeignNationalPassport[]>(
        `${BASE_URL}/${foreignNationalId}/passports`
      )
    ).then((rows) => (Array.isArray(rows) ? rows : []));
  },

  async getAutofill(foreignNationalId: string): Promise<AutofillProposals> {
    return recordRequest(() =>
      axiosInstance.get<AutofillProposals>(
        `${BASE_URL}/${foreignNationalId}/autofill`
      )
    );
  },

  async applyAutofill(
    foreignNationalId: string,
    body: AutofillApplyRequest
  ): Promise<{ rows: Array<{ kind: string; id: string; updated: boolean }> }> {
    return recordRequest(() =>
      axiosInstance.post<{
        rows: Array<{ kind: string; id: string; updated: boolean }>;
      }>(`${BASE_URL}/${foreignNationalId}/autofill`, body)
    );
  },

  async createPassport(
    foreignNationalId: string,
    payload: PassportPayload
  ): Promise<ForeignNationalPassport> {
    return recordRequest(() =>
      axiosInstance.post<ForeignNationalPassport>(
        `${BASE_URL}/${foreignNationalId}/passports`,
        payload
      )
    );
  },

  async updatePassport(
    foreignNationalId: string,
    passportId: string,
    payload: PassportPayload
  ): Promise<ForeignNationalPassport> {
    return recordRequest(() =>
      axiosInstance.put<ForeignNationalPassport>(
        `${BASE_URL}/${foreignNationalId}/passports/${passportId}`,
        payload
      )
    );
  },

  async deletePassport(
    foreignNationalId: string,
    passportId: string
  ): Promise<{ message?: string }> {
    return recordRequest(() =>
      axiosInstance.delete<{ message?: string }>(
        `${BASE_URL}/${foreignNationalId}/passports/${passportId}`
      )
    ).then((data) => data ?? {});
  },

  async listEducationHistoryEntries(
    foreignNationalId: string
  ): Promise<ForeignNationalEducationHistoryEntry[]> {
    return recordRequest(() =>
      axiosInstance.get<ForeignNationalEducationHistoryEntry[]>(
        `${BASE_URL}/${foreignNationalId}/education_history`
      )
    ).then((rows) => (Array.isArray(rows) ? rows : []));
  },

  async createEducationHistoryEntry(
    foreignNationalId: string,
    payload: EducationPayload
  ): Promise<ForeignNationalEducationHistoryEntry> {
    return recordRequest(() =>
      axiosInstance.post<ForeignNationalEducationHistoryEntry>(
        `${BASE_URL}/${foreignNationalId}/education_history`,
        payload
      )
    );
  },

  async updateEducationHistoryEntry(
    foreignNationalId: string,
    educationHistoryId: string,
    payload: EducationPayload
  ): Promise<ForeignNationalEducationHistoryEntry> {
    return recordRequest(() =>
      axiosInstance.put<ForeignNationalEducationHistoryEntry>(
        `${BASE_URL}/${foreignNationalId}/education_history/${educationHistoryId}`,
        payload
      )
    );
  },

  async deleteEducationHistoryEntry(
    foreignNationalId: string,
    educationHistoryId: string
  ): Promise<{ message?: string }> {
    return recordRequest(() =>
      axiosInstance.delete<{ message?: string }>(
        `${BASE_URL}/${foreignNationalId}/education_history/${educationHistoryId}`
      )
    ).then((data) => data ?? {});
  },

  /** The full SSN. Each call is recorded in the client's activity. */
  async revealSsn(foreignNationalId: string): Promise<string | null> {
    return recordRequest(() =>
      axiosInstance.get<{ ssn: string | null }>(
        `${BASE_URL}/${foreignNationalId}/ssn`
      )
    ).then((data) => data?.ssn ?? null);
  },

  async listImmigrationStatusHistoryEntries(
    foreignNationalId: string
  ): Promise<ForeignNationalImmigrationStatusHistoryEntry[]> {
    try {
      const response = await axiosInstance.get<
        ForeignNationalImmigrationStatusHistoryEntry[]
      >(`${BASE_URL}/${foreignNationalId}/immigration_status_history`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async createImmigrationStatusHistoryEntry(
    foreignNationalId: string,
    payload: CreateForeignNationalImmigrationStatusHistoryEntryRequest
  ): Promise<ForeignNationalImmigrationStatusHistoryEntry> {
    try {
      const response =
        await axiosInstance.post<ForeignNationalImmigrationStatusHistoryEntry>(
          `${BASE_URL}/${foreignNationalId}/immigration_status_history`,
          payload
        );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async updateImmigrationStatusHistoryEntry(
    foreignNationalId: string,
    immigrationStatusHistoryId: string,
    payload: UpdateForeignNationalImmigrationStatusHistoryEntryRequest
  ): Promise<ForeignNationalImmigrationStatusHistoryEntry> {
    try {
      const response =
        await axiosInstance.put<ForeignNationalImmigrationStatusHistoryEntry>(
          `${BASE_URL}/${foreignNationalId}/immigration_status_history/${immigrationStatusHistoryId}`,
          payload
        );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async deleteImmigrationStatusHistoryEntry(
    foreignNationalId: string,
    immigrationStatusHistoryId: string
  ): Promise<{ message?: string }> {
    try {
      const response = await axiosInstance.delete<{ message?: string }>(
        `${BASE_URL}/${foreignNationalId}/immigration_status_history/${immigrationStatusHistoryId}`
      );
      return response.data ?? {};
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async listImmigrationEntryExitHistoryEntries(
    foreignNationalId: string
  ): Promise<ForeignNationalImmigrationEntryExitHistoryEntry[]> {
    try {
      const response = await axiosInstance.get<
        ForeignNationalImmigrationEntryExitHistoryEntry[]
      >(`${BASE_URL}/${foreignNationalId}/immigration_entry_exit_history`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async createImmigrationEntryExitHistoryEntry(
    foreignNationalId: string,
    payload: CreateForeignNationalImmigrationEntryExitHistoryEntryRequest
  ): Promise<ForeignNationalImmigrationEntryExitHistoryEntry> {
    try {
      const response =
        await axiosInstance.post<ForeignNationalImmigrationEntryExitHistoryEntry>(
          `${BASE_URL}/${foreignNationalId}/immigration_entry_exit_history`,
          payload
        );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async updateImmigrationEntryExitHistoryEntry(
    foreignNationalId: string,
    immigrationEntryExitHistoryId: string,
    payload: UpdateForeignNationalImmigrationEntryExitHistoryEntryRequest
  ): Promise<ForeignNationalImmigrationEntryExitHistoryEntry> {
    try {
      const response =
        await axiosInstance.put<ForeignNationalImmigrationEntryExitHistoryEntry>(
          `${BASE_URL}/${foreignNationalId}/immigration_entry_exit_history/${immigrationEntryExitHistoryId}`,
          payload
        );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async deleteImmigrationEntryExitHistoryEntry(
    foreignNationalId: string,
    immigrationEntryExitHistoryId: string
  ): Promise<{ message?: string }> {
    try {
      const response = await axiosInstance.delete<{ message?: string }>(
        `${BASE_URL}/${foreignNationalId}/immigration_entry_exit_history/${immigrationEntryExitHistoryId}`
      );
      return response.data ?? {};
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** Pinned first, then newest. */
  async listNotes(
    foreignNationalId: string
  ): Promise<ForeignNationalNotesList> {
    try {
      const response = await axiosInstance.get<ForeignNationalNotesList>(
        `${BASE_URL}/${foreignNationalId}/notes`
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async createNote(
    foreignNationalId: string,
    payload: { body: string; pinned?: boolean; case_id?: string | null }
  ): Promise<ForeignNationalNote> {
    try {
      const response = await axiosInstance.post<ForeignNationalNote>(
        `${BASE_URL}/${foreignNationalId}/notes`,
        payload
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** The body is the author's to change; anyone with access pins. */
  async updateNote(
    foreignNationalId: string,
    noteId: string,
    patch: { body?: string; pinned?: boolean }
  ): Promise<ForeignNationalNote> {
    try {
      const response = await axiosInstance.patch<ForeignNationalNote>(
        `${BASE_URL}/${foreignNationalId}/notes/${noteId}`,
        patch
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** Warnings dismissed for the firm, newest first (stale ones included). */
  async listWarningDismissals(
    foreignNationalId: string
  ): Promise<ClientWarningDismissal[]> {
    try {
      const response = await axiosInstance.get<{
        dismissals: ClientWarningDismissal[];
      }>(`${BASE_URL}/${foreignNationalId}/warning_dismissals`);
      return response.data.dismissals ?? [];
    } catch (error) {
      throw handleAPIError(error);
    }
  },

  /** Hide a warning for the firm until its facts change; logged to activity. */
  async dismissWarning(
    foreignNationalId: string,
    payload: ClientWarningKey & { reason?: string | null }
  ): Promise<ClientWarningDismissal> {
    try {
      const response = await axiosInstance.post<ClientWarningDismissal>(
        `${BASE_URL}/${foreignNationalId}/warning_dismissals`,
        payload
      );
      return response.data;
    } catch (error) {
      throw handleAPIError(error);
    }
  },

  async restoreWarning(foreignNationalId: string, kind: string): Promise<void> {
    try {
      await axiosInstance.delete(
        `${BASE_URL}/${foreignNationalId}/warning_dismissals/${encodeURIComponent(
          kind
        )}`
      );
    } catch (error) {
      throw handleAPIError(error);
    }
  },

  async deleteNote(foreignNationalId: string, noteId: string): Promise<void> {
    try {
      await axiosInstance.delete(
        `${BASE_URL}/${foreignNationalId}/notes/${noteId}`
      );
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async listNoteProposals(
    foreignNationalId: string
  ): Promise<ForeignNationalNoteProposal[]> {
    try {
      const response = await axiosInstance.get<{
        proposals: ForeignNationalNoteProposal[];
      }>(`${BASE_URL}/${foreignNationalId}/notes/proposals`);
      return response.data.proposals ?? [];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** Answer a suggestion as its chat card would; the thread resumes and writes the entry on approval. */
  async answerNoteProposal(
    foreignNationalId: string,
    proposal: Pick<ForeignNationalNoteProposal, "approval_id" | "thread_id">,
    approved: boolean
  ): Promise<void> {
    try {
      await axiosInstance.post(
        `${BASE_URL}/${foreignNationalId}/notes/proposals/${proposal.approval_id}`,
        { thread_id: proposal.thread_id, approved }
      );
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** Notes across the clients the caller can open, newest first. */
  async searchNotes(q: string, limit = 5): Promise<NoteSearchResult[]> {
    try {
      const response = await axiosInstance.get<{ results: NoteSearchResult[] }>(
        "/notes/search",
        { params: { q, limit } }
      );
      return response.data.results ?? [];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async getAuthStatus(
    foreignNationalId: string
  ): Promise<ForeignNationalAuthStatus> {
    try {
      const response = await axiosInstance.get<ForeignNationalAuthStatus>(
        `${BASE_URL}/${foreignNationalId}/auth`
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async setAllowLogin(
    foreignNationalId: string,
    allowLogin: boolean
  ): Promise<ForeignNationalAuthStatus> {
    try {
      const response = await axiosInstance.put<ForeignNationalAuthStatus>(
        `${BASE_URL}/${foreignNationalId}/auth`,
        { allow_login: allowLogin }
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /**
   * One page of the client's activity feed, newest first. The server returns
   * `{items, next_cursor}`; an older array-shaped response is still accepted.
   */
  async listActivityLogPage(
    foreignNationalId: string,
    params?: ListForeignNationalActivityLogParams
  ): Promise<ForeignNationalActivityLogPage> {
    try {
      const response = await axiosInstance.get<
        ForeignNationalActivityLogPage | ForeignNationalActivityLogEvent[]
      >(`${BASE_URL}/${foreignNationalId}/activity-log`, { params });
      const { data } = response;
      if (Array.isArray(data)) return { items: data, next_cursor: null };
      return {
        items: Array.isArray(data?.items) ? data.items : [],
        next_cursor:
          typeof data?.next_cursor === "string" && data.next_cursor
            ? data.next_cursor
            : null
      };
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** The items of one page; see `listActivityLogPage` for the cursor. */
  async listActivityLog(
    foreignNationalId: string,
    params?: ListForeignNationalActivityLogParams
  ): Promise<ForeignNationalActivityLogEvent[]> {
    const page = await ForeignNationalsAPI.listActivityLogPage(
      foreignNationalId,
      params
    );
    return page.items;
  },

  async getActivitySubscription(
    foreignNationalId: string
  ): Promise<ForeignNationalActivitySubscription> {
    try {
      const response =
        await axiosInstance.get<ForeignNationalActivitySubscription>(
          `${BASE_URL}/${foreignNationalId}/activity-subscription`
        );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async subscribeActivity(
    foreignNationalId: string
  ): Promise<ForeignNationalActivitySubscription> {
    try {
      const response =
        await axiosInstance.put<ForeignNationalActivitySubscription>(
          `${BASE_URL}/${foreignNationalId}/activity-subscription`
        );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async unsubscribeActivity(
    foreignNationalId: string
  ): Promise<ForeignNationalActivitySubscription> {
    try {
      const response =
        await axiosInstance.delete<ForeignNationalActivitySubscription>(
          `${BASE_URL}/${foreignNationalId}/activity-subscription`
        );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  }
};
